import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    createChart,
    ColorType,
    LineData,
    Time,
    LineSeries,
    LineStyle,
    SeriesMarker,
    createSeriesMarkers,
    IChartApi,
    ISeriesApi,
    CandlestickSeries
} from 'lightweight-charts';
import { TimezoneMode, loadTimezone, saveTimezone, formatCrosshairLabel, formatTickMarkLabel } from '../../utils/timezone';
import { usePivotLevels } from './hooks/usePivotLevels';
import { usePriceData } from './hooks/usePriceData';
import { useInteractionSignals } from './hooks/useInteractionSignals';
import ExportButton from './components/ExportButton';
import SignalDetailModal from './components/SignalDetailModal';
import TimezoneControl from '../shared/TimezoneControl';
import { PIVOT_COLORS, LINE_WIDTHS } from './utils/colorPalette';
import { SIGNAL_TYPES, SIGNAL_CONFIG, getSignalMeta } from './utils/signalConfig';
import { calculateConvergence } from './utils/convergenceCalculator';
import { detectConvergencePeriods } from './utils/convergenceOverlay';
import { getPivotFetchRange, getInitialVisibleRange } from '../../utils/pivotDateRanges';
import { PivotTimeframe } from '../../types/pivotPoints.ts';
import { PivotSignalDataPoint } from '../../api/metrics';
import './PivotPointVisualizationChart.css';

interface PivotPointVisualizationChartProps {
  productId: string;
  timeframe: PivotTimeframe;  // Now required - controlled by parent
}

const PivotPointVisualizationChart: React.FC<PivotPointVisualizationChartProps> = ({ productId, timeframe }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const priceSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);

  const [showPrice, setShowPrice] = useState<boolean>(() => {
    return localStorage.getItem('pivot-chart-show-price') === 'true';
  });
  const [priceDisplayMode, setPriceDisplayMode] = useState<'line' | 'candles'>(() => {
    return (localStorage.getItem('pivot-chart-price-mode') as 'line' | 'candles') || 'line';
  });
  const [minSignalStrength, setMinSignalStrength] = useState<number>(0.75);
  const [selectedSignals, setSelectedSignals] = useState<PivotSignalDataPoint[]>([]);
  const [showConvergenceOverlay, setShowConvergenceOverlay] = useState<boolean>(() => {
    return localStorage.getItem('pivot-chart-show-convergence') !== 'false';
  });
  const [timezone, setTimezone] = useState<TimezoneMode>(loadTimezone());

  // Extended date ranges for fetching data (allows panning to see historical data)
  // while maintaining current zoom level on initial load
  const dateRange = useMemo(() =>
    getPivotFetchRange(timeframe),
    [timeframe]
  );

  // Calculate visible range for initial zoom (what was previously the full date range)
  const initialVisibleRange = useMemo(() =>
    getInitialVisibleRange(timeframe),
    [timeframe]
  );

  const { data: pivotData, isLoading, isError, error } = usePivotLevels(
    productId,
    timeframe,
    dateRange.start,
    dateRange.end
  );

  // Candle granularity should match the interaction detection granularity
  // This ensures candles align with how pivot interactions are detected
  const priceGranularity = useMemo(() => {
    switch (timeframe) {
      case 'THIRTY_MIN': return 5;     // 5-minute candles
      case 'ONE_HOUR': return 5;       // 5-minute candles
      case 'TWO_HOUR': return 5;       // 5-minute candles
      case 'FOUR_HOUR': return 15;     // 15-minute candles
      case 'EIGHT_HOUR': return 15;    // 15-minute candles
      case 'TWELVE_HOUR': return 15;   // 15-minute candles (was showing 1-min)
      case 'TWENTY_FOUR_HOUR': return 60;  // 1-hour candles
      case 'SEVEN_DAY': return 240;    // 4-hour candles
      case 'THIRTY_DAY': return 240;   // 4-hour candles
      default: return 1;
    }
  }, [timeframe]);

  // Calculate actual date range from pivot data for price fetching
  // Important: Extend to current time since the last pivot point is valid until now
  const priceDateRange = useMemo(() => {
    if (!pivotData || pivotData.length === 0) {
      return dateRange;
    }

    // Find min timestamp from pivot data, but use current time as end
    // This ensures we show candles up to the current moment
    const timestamps = pivotData.map(d => d.timestamp);
    const minTimestamp = Math.min(...timestamps);

    return {
      start: new Date(minTimestamp),
      end: new Date(), // Current time - the last pivot is still valid
    };
  }, [pivotData, dateRange]);

  const { data: priceData } = usePriceData(
    productId,
    priceGranularity,
    priceDateRange.start,
    priceDateRange.end,
    showPrice
  );

  // Fetch pivot interaction signals (only when price is shown)
  // Filter by timeframe to avoid showing overlapping signals from multiple timeframes
  const { data: signalsData } = useInteractionSignals(
    productId,
    dateRange.start,
    dateRange.end,
    minSignalStrength,
    showPrice,
    timeframe
  );

  const convergenceInfo = useMemo(() => {
    if (!pivotData || pivotData.length === 0) return null;
    const latest = pivotData[pivotData.length - 1];
    return calculateConvergence(latest.r1, latest.s1, latest.pp);
  }, [pivotData]);

  const convergencePeriods = useMemo(() => {
    if (!pivotData || pivotData.length === 0 || !showConvergenceOverlay) return [];
    return detectConvergencePeriods(pivotData);
  }, [pivotData, showConvergenceOverlay]);


  useEffect(() => {
    localStorage.setItem('pivot-chart-show-price', showPrice.toString());
  }, [showPrice]);

  useEffect(() => {
    localStorage.setItem('pivot-chart-price-mode', priceDisplayMode);
  }, [priceDisplayMode]);

  useEffect(() => {
    localStorage.setItem('pivot-chart-show-convergence', showConvergenceOverlay.toString());
  }, [showConvergenceOverlay]);

  // Initialize chart and load data - exactly like CandlestickChart
  useEffect(() => {
    if (!chartContainerRef.current || isLoading || !pivotData || pivotData.length === 0) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#0f172a' },
        textColor: '#94a3b8',
      },
      grid: {
        vertLines: { color: '#1e293b' },
        horzLines: { color: '#1e293b' },
      },
      crosshair: {
        mode: 1,
        vertLine: {
          width: 1,
          color: '#8B5CF6',
          style: 2,
          labelBackgroundColor: '#8B5CF6',
        },
        horzLine: {
          width: 1,
          color: '#8B5CF6',
          style: 2,
          labelBackgroundColor: '#8B5CF6',
        },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: '#334155',
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          formatTickMarkLabel(time as number, tickMarkType, timezone),
      },
      localization: {
        timeFormatter: (time: number) => formatCrosshairLabel(time, timezone),
        priceFormatter: (price: number) => {
          return productId === 'ADA-USDC'
            ? price.toFixed(4)
            : price.toFixed(2);
        },
      },
      rightPriceScale: {
        borderColor: '#334155',
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
      },
      handleScale: {
        mouseWheel: true,
        pinch: true,
      },
    });

    chartRef.current = chart;

    // Add line series for each pivot level
    const r3Series = chart.addSeries(LineSeries, {
      color: PIVOT_COLORS.R3,
      lineWidth: LINE_WIDTHS.R3,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    const r2Series = chart.addSeries(LineSeries, {
      color: PIVOT_COLORS.R2,
      lineWidth: LINE_WIDTHS.R2,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    const r1Series = chart.addSeries(LineSeries, {
      color: PIVOT_COLORS.R1,
      lineWidth: LINE_WIDTHS.R1,
      lineStyle: LineStyle.Dashed,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    const ppSeries = chart.addSeries(LineSeries, {
      color: PIVOT_COLORS.PP,
      lineWidth: LINE_WIDTHS.PP,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    const s1Series = chart.addSeries(LineSeries, {
      color: PIVOT_COLORS.S1,
      lineWidth: LINE_WIDTHS.S1,
      lineStyle: LineStyle.Dashed,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    const s2Series = chart.addSeries(LineSeries, {
      color: PIVOT_COLORS.S2,
      lineWidth: LINE_WIDTHS.S2,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    const s3Series = chart.addSeries(LineSeries, {
      color: PIVOT_COLORS.S3,
      lineWidth: LINE_WIDTHS.S3,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    // Convert and set pivot data
    // Important: Extend the last pivot point to current time since it's still valid
    const convertData = (key: string): LineData[] => {
      const baseData = pivotData
        .filter(d => (d as any)[key] !== undefined && (d as any)[key] !== null)
        .map(d => ({
          time: (d.timestamp / 1000) as Time,
          value: (d as any)[key],
        }));

      // Add a point at current time with the last pivot value to extend the line
      // Truncate to whole minutes (remove seconds) for cleaner display
      // IMPORTANT: Only add if it's a different timestamp to avoid duplicates
      if (baseData.length > 0) {
        const lastValue = baseData[baseData.length - 1].value;
        const lastTime = baseData[baseData.length - 1].time;
        const now = new Date();
        now.setSeconds(0, 0); // Truncate seconds and milliseconds
        const currentTime = (now.getTime() / 1000) as Time;

        // Only push if current time is different from the last data point
        if (currentTime !== lastTime) {
          baseData.push({
            time: currentTime,
            value: lastValue,
          });
        }
      }

      return baseData;
    };

    r3Series.setData(convertData('r3'));
    r2Series.setData(convertData('r2'));
    r1Series.setData(convertData('r1'));
    ppSeries.setData(convertData('pp'));
    s1Series.setData(convertData('s1'));
    s2Series.setData(convertData('s2'));
    s3Series.setData(convertData('s3'));

    // Add convergence period overlays if enabled
    // We'll add subtle markers at the top of the chart to indicate convergence zones
    if (showConvergenceOverlay && convergencePeriods.length > 0 && pivotData.length > 0) {
        const priceMax = Math.max(...pivotData.map(d => d.r3));
        const priceMin = Math.min(...pivotData.map(d => d.s3));

      // For each convergence period, add markers at start and end
      // Filter out periods where start === end (single data point periods)
      convergencePeriods
        .filter(period => period.startTime !== period.endTime)
        .forEach(period => {
          const zoneTopSeries = chart.addSeries(LineSeries, {
            color: period.level === 'tight' ? 'rgba(255, 140, 0, 0.6)' : 'rgba(255, 215, 0, 0.5)',
            lineWidth: 3,
            lineStyle: LineStyle.Solid,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
          });


            const zoneBottomSeries = chart.addSeries(LineSeries, {
                color: period.level === 'tight' ? 'rgba(255, 140, 0, 0.6)' : 'rgba(255, 215, 0, 0.5)',
                lineWidth: 3,
                lineStyle: LineStyle.Solid,
                priceLineVisible: false,
                lastValueVisible: false,
                crosshairMarkerVisible: false,
            });

          // Create a horizontal line at the top of the chart spanning the convergence period
          // Use unique timestamps only
          const zoneTopData: LineData[] = [
              { time: (period.startTime / 1000) as Time, value: priceMax * 1.001 },
              { time: (period.endTime / 1000) as Time, value: priceMax * 1.001 },
          ];

            // Create a horizontal line at the top of the chart spanning the convergence period
            // Use unique timestamps only
            const zoneBottomData: LineData[] = [
                { time: (period.startTime / 1000) as Time, value: priceMin * 0.999 },
                { time: (period.endTime / 1000) as Time, value: priceMin * 0.999 },
            ];

          zoneTopSeries.setData(zoneTopData);
          zoneBottomSeries.setData(zoneBottomData);
        });
    }

    // Add price series if enabled
    if (showPrice && priceData?.candles && priceData.candles.length > 0) {
      if (priceDisplayMode === 'candles') {
        // Candlestick chart
        const candlestickSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#10b981',
            downColor: '#ef4444',
            borderVisible: false,
            wickUpColor: '#10b981',
            wickDownColor: '#ef4444',
        });

        priceSeriesRef.current = candlestickSeries as any;

        const candleData = priceData.candles
          .filter(c => c.open && c.high && c.low && c.close && c.timestamp)
          .map(c => ({
            time: (new Date(c.timestamp).getTime() / 1000) as Time,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
          }));

        candlestickSeries.setData(candleData);

      // Add signal markers if we have signal data
      // API already filters out TOUCH signals - only BOUNCE, REJECTION, and BREAK variants are returned
      if (signalsData?.signals && signalsData.signals.length > 0) {
        const markers: SeriesMarker<Time>[] = signalsData.signals.map(signal => {
          const signalMeta = getSignalMeta(signal.type);

          // Use config if available, otherwise default
          const shape = signalMeta?.chartShape || 'circle';
          const position = signalMeta?.chartPosition || 'inBar';
          const color = signalMeta?.chartColor || '#808080';

          return {
            time: (new Date(signal.timestamp).getTime() / 1000) as Time,
            position: position,
            color: color,
            shape: shape,
            size: signal.strength > 0.7 ? 0.75 : 0.5,
          };
        });
        createSeriesMarkers(candlestickSeries as any, markers);
      }
      } else {
        // Line chart (existing implementation)
        const priceSeries = chart.addSeries(LineSeries, {
          color: PIVOT_COLORS.PRICE,
          lineWidth: LINE_WIDTHS.PRICE,
          priceLineVisible: false,
          lastValueVisible: true,
        });

        priceSeriesRef.current = priceSeries;

        const priceLineData: LineData[] = priceData.candles
          .filter(c => c.close && c.timestamp)
          .map(c => ({
            time: (new Date(c.timestamp).getTime() / 1000) as Time,
            value: c.close,
          }));

        priceSeries.setData(priceLineData);

        // Add signal markers if we have signal data
        if (signalsData?.signals && signalsData.signals.length > 0) {
          const markers: SeriesMarker<Time>[] = signalsData.signals.map(signal => {
            const signalMeta = getSignalMeta(signal.type);

            // Use config if available, otherwise default
            const shape = signalMeta?.chartShape || 'circle';
            const position = signalMeta?.chartPosition || 'inBar';
            const color = signalMeta?.chartColor || '#808080';

            return {
              time: (new Date(signal.timestamp).getTime() / 1000) as Time,
              position: position,
              color: color,
              shape: shape,
              size: signal.strength > 0.7 ? 0.75 : 0.5,
            };
          });
          createSeriesMarkers(priceSeries, markers);
        }
      }
    }


    // Set initial visible range (zoomed to recent data, but full dataset is loaded)
    // This allows panning backward to see historical data
    const fromTimestamp = (initialVisibleRange.start.getTime() / 1000) as Time;
    const toTimestamp = (initialVisibleRange.end.getTime() / 1000) as Time;

    chart.timeScale().setVisibleRange({
      from: fromTimestamp,
      to: toTimestamp,
    });

    // Add click handler for signal markers
    const handleChartClick = (param: any) => {
      if (!param.time || !signalsData?.signals) return;

      // Convert clicked time to timestamp
      const clickedTime = (param.time as number) * 1000;

      // Find signal near the clicked time
      // Use dynamic tolerance based on timeframe
      let tolerance: number;
      switch (timeframe) {
        case 'THIRTY_MIN': tolerance = 5 * 60 * 1000; break;  // 5 minutes
        case 'ONE_HOUR': tolerance = 10 * 60 * 1000; break;   // 10 minutes
        case 'TWO_HOUR': tolerance = 15 * 60 * 1000; break;   // 15 minutes
        case 'FOUR_HOUR': tolerance = 30 * 60 * 1000; break;  // 30 minutes
        case 'EIGHT_HOUR': tolerance = 60 * 60 * 1000; break; // 1 hour
        case 'TWELVE_HOUR': tolerance = 90 * 60 * 1000; break; // 1.5 hours
        case 'TWENTY_FOUR_HOUR': tolerance = 2 * 60 * 60 * 1000; break; // 2 hours
        case 'SEVEN_DAY': tolerance = 4 * 60 * 60 * 1000; break; // 4 hours
        case 'THIRTY_DAY': tolerance = 12 * 60 * 60 * 1000; break; // 12 hours
        default: tolerance = 5 * 60 * 1000;
      }

      // Find ALL signals near the clicked time (not just the first one)
      const clickedSignals = signalsData.signals.filter(signal => {
        const signalTime = new Date(signal.timestamp).getTime();
        return Math.abs(signalTime - clickedTime) < tolerance;
      });

      if (clickedSignals.length > 0) {
        setSelectedSignals(clickedSignals);
      }
    };

    chart.subscribeClick(handleChartClick);

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.unsubscribeClick(handleChartClick);
      chart.remove();
      chartRef.current = null;
      priceSeriesRef.current = null;
    };
  // remove timezone from deps to avoid re-creating chart on toggle
  }, [isLoading, pivotData, priceData, showPrice, priceDisplayMode, signalsData, showConvergenceOverlay, convergencePeriods, initialVisibleRange, productId, timeframe]);

  // Apply timezone-specific formatting without re-creating the chart
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      localization: { timeFormatter: (t: number) => formatCrosshairLabel(t, timezone) },
      timeScale: { tickMarkFormatter: (time: Time, tickMarkType: number) => formatTickMarkLabel(time as number, tickMarkType, timezone) },
    });
    saveTimezone(timezone);
  }, [timezone]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="pivot-chart-container">
        <div className="pivot-chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading pivot level data...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (isError) {
    return (
      <div className="pivot-chart-container">
        <div className="pivot-chart-error">
          <p className="error-title">Error Loading Pivot Data</p>
          <p className="error-message">{error?.message || 'An unknown error occurred'}</p>
        </div>
      </div>
    );
  }

  if (!pivotData || pivotData.length === 0) {
    return (
      <div className="pivot-chart-container">
        <div className="pivot-chart-empty">
          <p>No pivot level data available for the selected timeframe.</p>
        </div>
      </div>
    );
  }

  // Main chart rendering
  return (
    <div className="pivot-chart-container">

      <div className="pivot-chart-controls">

        <div className="control-group">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={showPrice}
              onChange={(e) => setShowPrice(e.target.checked)}
              className="price-toggle"
            />
            <span>Show Price</span>
          </label>
        </div>

        {showPrice && (
          <div className="control-group">
            <label htmlFor="price-mode-select">Price Display:</label>
            <select
              id="price-mode-select"
              value={priceDisplayMode}
              onChange={(e) => setPriceDisplayMode(e.target.value as 'line' | 'candles')}
              className="timeframe-select"
            >
              <option value="line">Line Chart</option>
              <option value="candles">Candlesticks</option>
            </select>
          </div>
        )}

        <div className="control-group">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={showConvergenceOverlay}
              onChange={(e) => setShowConvergenceOverlay(e.target.checked)}
              className="convergence-toggle"
            />
            <span
              title="Highlights periods when pivot levels are converging into tight ranges, indicating potential breakouts"
            >
              Confluence Zones
            </span>
          </label>
        </div>

        {showPrice && (
          <div className="control-group">
            <label htmlFor="signal-strength">Min Confidence:</label>
            <input
              id="signal-strength"
              type="range"
              min="0.65"
              max="0.95"
              step="0.05"
              value={minSignalStrength}
              onChange={(e) => setMinSignalStrength(parseFloat(e.target.value))}
              className="signal-strength-slider"
              title={`Minimum signal confidence: ${(minSignalStrength * 100).toFixed(0)}%`}
            />
            <span className="signal-strength-value">{(minSignalStrength * 100).toFixed(0)}%</span>
          </div>
        )}

        {convergenceInfo && convergenceInfo.level !== 'none' && (
          <div 
            className={`convergence-badge ${convergenceInfo.level === 'tight' ? 'tight' : 'moderate'}`}
            title={`Convergence ratio: ${(convergenceInfo.ratio * 100).toFixed(2)}% - Calculated as (R1-S1)/PP. Lower values indicate tighter ranges and potential breakouts.`}
          >
            {convergenceInfo.level === 'tight' ? 'Tight Range' : 'Convergence'}
            <span className="convergence-ratio">
              {(convergenceInfo.ratio * 100).toFixed(2)}%
            </span>
          </div>
        )}

        <div className="control-group control-group-right">
          <button
            className="reset-button"
            onClick={() => {
              if (chartRef.current) {
                const fromTimestamp = (initialVisibleRange.start.getTime() / 1000) as Time;
                const toTimestamp = (initialVisibleRange.end.getTime() / 1000) as Time;
                chartRef.current.timeScale().setVisibleRange({
                  from: fromTimestamp,
                  to: toTimestamp,
                });
              }
            }}
            title="Reset zoom to default view"
          >
            <span className="reset-icon">⟲</span>
            Reset
          </button>

          <ExportButton
            chartRef={chartContainerRef}
            pivotData={pivotData}
            productId={productId}
            timeframe={timeframe}
            showPrice={showPrice}
            priceData={priceData?.candles}
          />
        </div>
      </div>

      {/* Timezone Control */}
      <TimezoneControl timezone={timezone} onTimezoneChange={setTimezone} />

      <div
        ref={chartContainerRef}
        style={{
          width: '100%',
          height: 400,
          position: 'relative'
        }}
      />

      <div className="pivot-chart-info">
        <span className="data-points-count">
          {pivotData.length} data points
        </span>
        <span className="date-range">
          {dateRange.start.toLocaleDateString()} - {dateRange.end.toLocaleDateString()}
        </span>
        {showPrice && signalsData && signalsData.signals && signalsData.signals.length > 0 && (
          <span className="signal-count">
            {signalsData.signals.length} signals
          </span>
        )}
        {showConvergenceOverlay && convergencePeriods.length > 0 && (
          <span className="convergence-count">
            {convergencePeriods.length} confluence zone{convergencePeriods.length !== 1 ? 's' : ''}
          </span>
        )}
        <span className="chart-tip">
          💡 Use mouse wheel to zoom, click and drag to pan{showPrice && ', click markers for details'}
        </span>
      </div>

      {showPrice && (
        <div className="signal-legend">
          <span className="legend-title">Signals:</span>
          {SIGNAL_TYPES.map(signalType => {
            const meta = SIGNAL_CONFIG[signalType];
            const Icon = meta.Icon;
            return (
              <span key={meta.key} className="legend-item" data-signal-type={meta.key.toLowerCase()}>
                <Icon size={12} />
                <span>{meta.label}</span>
              </span>
            );
          })}
        </div>
      )}

      <SignalDetailModal
        signals={selectedSignals}
        onClose={() => setSelectedSignals([])}
        timezone={timezone}
      />
    </div>
  );
};

export default PivotPointVisualizationChart;

